/**
 * CapstonePlayer — learner-facing capstone project submission component.
 *
 * Renders the capstone brief as markdown, shows deliverable requirements,
 * handles file uploads, text/URL inputs, and submissions. Displays grading
 * status and feedback after evaluation.
 */
import React, { useState, useEffect, useCallback } from 'react';
import SubmissionFeedback from '../rubrics/SubmissionFeedback';

const STATUS_BADGES = {
  pending: { bg: '#fef3c7', color: '#92400e', label: 'Queued for AI Grading' },
  pending_ai_grade: { bg: '#fef3c7', color: '#92400e', label: 'AI Grading in Progress' },
  ai_graded: { bg: '#dbeafe', color: '#1e40af', label: 'AI Graded — Passed' },
  ai_failed: { bg: '#fee2e2', color: '#991b1b', label: 'Below Pass Score — Resubmit' },
  grading_failed: { bg: '#fee2e2', color: '#991b1b', label: 'Grading Unavailable — Retry Soon' },
  coach_review_pending: { bg: '#e0f2fe', color: '#0369a1', label: 'Pending Coach Review' },
  coach_approved: { bg: '#dcfce7', color: '#166534', label: 'Coach Approved' },
  resubmit_requested: { bg: '#ffedd5', color: '#c2410c', label: 'Revision Requested' },
  reviewed: { bg: '#dcfce7', color: '#166534', label: 'Reviewed' },
};

export default function CapstonePlayer({ segment, domain, sceneType = 'capstone', onComplete }) {
  const metadata = segment.domain_metadata || segment.scene_data?.domain_metadata || segment.metadata || {};
  const briefMd = metadata.brief_md || segment.brief_md || segment.brief
    || metadata.instructions_md || segment.instructions_md || segment.description || '';
  // Authoring may store structured {type,label} objects OR plain requirement strings.
  // Normalize so render never assumes dt.type exists (string → TypeError on .charAt).
  const rawDeliverables = metadata.deliverable_types || segment.deliverable_types
    || segment.deliverables || [];
  const deliverableTypes = (Array.isArray(rawDeliverables) ? rawDeliverables : []).map((dt) => {
    if (typeof dt === 'string') return { type: 'text', label: dt };
    if (dt && typeof dt === 'object') return { type: dt.type || 'text', ...dt };
    return { type: 'text', label: String(dt ?? '') };
  });
  const gradingConfig = metadata.grading_config || segment.grading_config || {};
  const maxSubmissions = gradingConfig.max_submissions || metadata.max_submissions || 3;
  const dueDate = metadata.due_date || null;
  const submissionInstructions = metadata.submission_instructions || '';
  const sceneId = segment.scene_id || segment.segment_id;

  const [text, setText] = useState('');
  const [fileUrls, setFileUrls] = useState([]);
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submission, setSubmission] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('scenario_os_token');

  const fetchSubmissions = useCallback(async () => {
    if (!token || !sceneId) { setLoading(false); return; }
    try {
      const res = await fetch(
        `/scenario-os/curriculum/capstone/${encodeURIComponent(sceneId)}/submissions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        const subs = data.submissions || [];
        setSubmissions(subs);
        if (subs.length > 0) setSubmission(subs[0]);
      }
    } catch { /* non-critical */ }
    setLoading(false);
  }, [token, sceneId]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  // Poll while AI grade is queued — AgentJobQueue grades asynchronously
  useEffect(() => {
    const st = submission?.status;
    if (!st || !['pending', 'pending_ai_grade'].includes(st)) return undefined;
    const id = setInterval(() => { fetchSubmissions(); }, 5000);
    return () => clearInterval(id);
  }, [submission?.status, fetchSubmissions]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setError('');

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('scene_id', sceneId);
      formData.append('domain_id', domain || 'ai');
      try {
        const res = await fetch('/scenario-os/learner/submissions/upload', {
          method: 'POST',
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          const fileUrl = data.storage_uri || data.url || data.file_url || data.download_url || '';
          if (fileUrl) setFileUrls((prev) => [...prev, fileUrl]);
        } else {
          setError(`Failed to upload ${file.name}`);
        }
      } catch (err) {
        setError(`Upload error: ${err.message}`);
      }
    }
    setUploading(false);
  };

  const handleSubmit = async () => {
    if (!text && fileUrls.length === 0 && !url) {
      setError('Please provide at least one deliverable.');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      const res = await fetch(`/scenario-os/curriculum/capstone/${encodeURIComponent(sceneId)}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          text: text || '',
          file_url: fileUrls[0] || null,
          file_urls: fileUrls.length > 0 ? fileUrls : null,
          url: url || null,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSubmission(data);
      setSubmissions((prev) => [data, ...prev]);
      setText('');
      setFileUrls([]);
      setUrl('');
      if (typeof onComplete === 'function') onComplete();
    } catch (err) {
      setError(err.message || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const attemptNumber = submissions.length;
  const canSubmit = attemptNumber < maxSubmissions;
  const hasGrade = submission && [
    'reviewed', 'ai_graded', 'ai_failed', 'coach_review_pending',
    'coach_approved', 'resubmit_requested',
  ].includes(submission.status);
  const awaitingAi = submission && ['pending', 'pending_ai_grade'].includes(submission.status);

  // Accepted formats for file input
  const fileDeliverable = deliverableTypes.find((dt) => dt.type === 'file');
  const acceptFormats = (fileDeliverable?.allowed_extensions || fileDeliverable?.accepted_formats || []).join(',');

  if (loading) {
    return <div style={{ color: '#6b7280', fontSize: 14, padding: '24px 0' }}>Loading capstone…</div>;
  }

  if (showFeedback && submission?.submission_id) {
    return (
      <div>
        <button onClick={() => setShowFeedback(false)} style={linkBtnStyle}>
          ← Back to Capstone
        </button>
        <SubmissionFeedback
          submissionId={submission.submission_id}
          onClose={() => setShowFeedback(false)}
        />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Header */}
      <div style={headerBadgeStyle}>
        <span style={{ fontSize: 24 }}>🎓</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>
            {sceneType === 'project' ? 'Project Submission' : 'Capstone Project'}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            Complete and submit your project deliverables
          </div>
        </div>
      </div>

      {/* Due date countdown */}
      {dueDate && <DueDateBanner dueDate={dueDate} />}

      {/* Brief */}
      <div style={briefCardStyle}>
        <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, color: '#475569' }}>
          Project Brief
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap', color: '#0f172a' }}>
          {briefMd.split('\n').map((line, i) => {
            if (line.startsWith('## ')) return <h3 key={i} style={{ margin: '12px 0 6px', fontSize: 16, fontWeight: 700 }}>{line.slice(3)}</h3>;
            if (line.startsWith('### ')) return <h4 key={i} style={{ margin: '8px 0 4px', fontSize: 14, fontWeight: 700 }}>{line.slice(4)}</h4>;
            if (line.startsWith('- ')) return <li key={i} style={{ marginLeft: 16, marginBottom: 3 }}>{line.slice(2)}</li>;
            if (line.startsWith('**') && line.endsWith('**')) return <p key={i} style={{ fontWeight: 600, margin: '4px 0' }}>{line.slice(2, -2)}</p>;
            if (line === '') return <br key={i} />;
            return <span key={i}>{line}<br /></span>;
          })}
        </div>
      </div>

      {/* Submission instructions */}
      {submissionInstructions && (
        <div style={{ ...briefCardStyle, background: '#fef3c7', border: '1px solid #fde68a', marginTop: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, color: '#92400e' }}>
            Submission Instructions
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.6, color: '#78350f' }}>{submissionInstructions}</div>
        </div>
      )}

      {/* Deliverable requirements */}
      {deliverableTypes.length > 0 && (
        <div style={requirementsCardStyle}>
          <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10, color: '#475569' }}>
            Required Deliverables
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {deliverableTypes.map((dt, i) => {
              const dtype = dt.type || 'text';
              const label = dt.label
                || (dtype.charAt(0).toUpperCase() + dtype.slice(1));
              return (
              <div key={i} style={deliverableRowStyle}>
                <span style={deliverableIconStyle}>{dtype === 'file' ? '📎' : dtype === 'url' ? '🔗' : '📝'}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {label}
                  </div>
                  {dtype === 'file' && (dt.allowed_extensions || dt.accepted_formats) && (
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      Formats: {(dt.allowed_extensions || dt.accepted_formats).join(', ')} {dt.max_size_mb ? `· Max ${dt.max_size_mb}MB` : ''}
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Attempt counter */}
      <div style={attemptCounterStyle}>
        <span style={{ fontSize: 13, color: '#475569' }}>
          Attempt {attemptNumber} of {maxSubmissions}
        </span>
        {!canSubmit && (
          <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
            Maximum submissions reached
          </span>
        )}
      </div>

      {/* Submission form */}
      {canSubmit && (
        <div style={formCardStyle}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: '#1e293b' }}>
            Submit Your Work
          </div>

          {/* Text input */}
          {deliverableTypes.some((dt) => dt.type === 'text') && (
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Written Response</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Write your capstone response here…"
                rows={8}
                style={textareaStyle}
              />
            </div>
          )}

          {/* File upload */}
          {deliverableTypes.some((dt) => dt.type === 'file') && (
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Upload Files</label>
              <div style={uploadAreaStyle}>
                <input
                  type="file"
                  multiple
                  accept={acceptFormats}
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  id="capstone-file-input"
                />
                <label htmlFor="capstone-file-input" style={uploadLabelStyle}>
                  {uploading ? 'Uploading…' : '📁 Choose files to upload'}
                </label>
              </div>
              {fileUrls.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {fileUrls.map((fu, i) => (
                    <div key={i} style={fileChipStyle}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        📎 {fu.split('/').pop() || fu}
                      </span>
                      <button
                        onClick={() => setFileUrls((prev) => prev.filter((_, idx) => idx !== i))}
                        style={removeChipBtnStyle}
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* URL input */}
          {deliverableTypes.some((dt) => dt.type === 'url') && (
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>URL Link</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                style={inputStyle}
              />
            </div>
          )}

          {/* If no specific types, show all inputs */}
          {deliverableTypes.length === 0 && (
            <>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Written Response</label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Write your capstone response here…"
                  rows={6}
                  style={textareaStyle}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>URL Link (optional)</label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://..."
                  style={inputStyle}
                />
              </div>
            </>
          )}

          {error && <div style={errorStyle}>{error}</div>}

          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={submitBtnStyle}
          >
            {submitting ? 'Submitting…' : sceneType === 'project' ? '🚀 Submit Project' : '🚀 Submit Capstone'}
          </button>
        </div>
      )}

      {/* Submission status */}
      {submission && (
        <div style={statusCardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 18 }}>✅</span>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>
              Submission Received
            </span>
            {submission.status && (
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 999,
                background: (STATUS_BADGES[submission.status] || STATUS_BADGES.pending).bg,
                color: (STATUS_BADGES[submission.status] || STATUS_BADGES.pending).color,
              }}>
                {(STATUS_BADGES[submission.status] || STATUS_BADGES.pending).label}
              </span>
            )}
            {submission.gate_status && (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999,
                background: submission.gate_status === 'passed' ? '#dcfce7' : '#fee2e2',
                color: submission.gate_status === 'passed' ? '#166534' : '#991b1b',
              }}>
                {submission.gate_status === 'passed' ? '🏆 PASSED' : '❌ NOT PASSED'}
              </span>
            )}
          </div>

          {awaitingAi && (
            <p style={{ margin: '8px 0 0', fontSize: 13, color: '#92400e', lineHeight: 1.5 }}>
              Your submission is queued for AI grading. This page refreshes automatically —
              scores and feedback appear when the grading agent finishes (no placeholder scores).
            </p>
          )}

          {submission.status === 'ai_failed' && (
            <p style={{ margin: '8px 0 0', fontSize: 13, color: '#991b1b', lineHeight: 1.5 }}>
              Score is below the pass threshold. Review the feedback and resubmit while attempts remain.
            </p>
          )}

          {hasGrade && (
            <button onClick={() => setShowFeedback(true)} style={viewFeedbackBtnStyle}>
              View Detailed Feedback
            </button>
          )}
        </div>
      )}

      {/* Previous attempts */}
      {submissions.length > 1 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#475569', marginBottom: 8 }}>
            Previous Attempts
          </div>
          {submissions.slice(1).map((sub) => (
            <div key={sub.submission_id} style={historyRowStyle}>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                Attempt {sub.attempt_number || '—'}
              </span>
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999,
                background: (STATUS_BADGES[sub.status] || STATUS_BADGES.pending).bg,
                color: (STATUS_BADGES[sub.status] || STATUS_BADGES.pending).color,
              }}>
                {(STATUS_BADGES[sub.status] || STATUS_BADGES.pending).label}
              </span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>
                {sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function DueDateBanner({ dueDate }) {
  const due = new Date(dueDate);
  const now = new Date();
  const diffMs = due - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  const isOverdue = diffMs < 0;
  const isUrgent = diffDays <= 3 && !isOverdue;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '10px 14px', borderRadius: 8, marginBottom: 14,
      background: isOverdue ? '#fee2e2' : isUrgent ? '#fef3c7' : '#f0fdf4',
      border: `1px solid ${isOverdue ? '#fecaca' : isUrgent ? '#fde68a' : '#bbf7d0'}`,
      fontSize: 13, fontWeight: 600,
      color: isOverdue ? '#991b1b' : isUrgent ? '#92400e' : '#166534',
    }}>
      <span>{isOverdue ? '⚠️' : '⏰'}</span>
      {isOverdue ? (
        <span>Overdue — was due {due.toLocaleDateString()}</span>
      ) : (
        <span>Due {due.toLocaleDateString()} — {diffDays} day{diffDays !== 1 ? 's' : ''} remaining</span>
      )}
    </div>
  );
}


// ── Styles ───────────────────────────────────────────────────────────────────

const headerBadgeStyle = {
  display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
  padding: '12px 16px',
  background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
  borderRadius: 10, color: '#e2e8f0',
};

const briefCardStyle = {
  background: '#f8fafc', border: '1px solid #e2e8f0',
  borderRadius: 10, padding: 20, marginBottom: 14,
};

const requirementsCardStyle = {
  background: '#fff', border: '1px solid #e2e8f0',
  borderRadius: 10, padding: 16, marginBottom: 14,
};

const deliverableRowStyle = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '8px 12px', background: '#f1f5f9', borderRadius: 8,
};

const deliverableIconStyle = { fontSize: 18 };

const attemptCounterStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '8px 0', marginBottom: 8,
};

const formCardStyle = {
  background: '#fff', border: '1px solid #e2e8f0',
  borderRadius: 10, padding: 20, marginBottom: 14,
};

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600, color: '#475569',
  marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3,
};

const textareaStyle = {
  width: '100%', padding: 12, border: '1px solid #d1d5db',
  borderRadius: 8, fontSize: 14, fontFamily: 'inherit',
  lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box',
  background: '#fff', color: '#0f172a',
};

const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
  borderRadius: 8, fontSize: 14, fontFamily: 'inherit',
  boxSizing: 'border-box', background: '#fff', color: '#0f172a',
};

const uploadAreaStyle = {
  border: '2px dashed #d1d5db', borderRadius: 8,
  padding: '16px 20px', textAlign: 'center', cursor: 'pointer',
};

const uploadLabelStyle = {
  cursor: 'pointer', fontSize: 14, color: '#3b82f6', fontWeight: 600,
};

const fileChipStyle = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '6px 10px', background: '#eff6ff', borderRadius: 6,
  fontSize: 12, color: '#1e40af', marginBottom: 4,
};

const removeChipBtnStyle = {
  background: 'none', border: 'none', color: '#6b7280',
  cursor: 'pointer', fontSize: 14, padding: '0 4px',
};

const errorStyle = {
  color: '#dc2626', fontSize: 13, marginBottom: 10,
  padding: '8px 12px', background: '#fef2f2', borderRadius: 6,
};

const submitBtnStyle = {
  width: '100%', padding: '12px 20px',
  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
  color: '#fff', border: 'none', borderRadius: 8,
  fontSize: 15, fontWeight: 700, cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
};

const statusCardStyle = {
  background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
  border: '1px solid #bbf7d0', borderRadius: 10,
  padding: 16, marginTop: 14,
};

const viewFeedbackBtnStyle = {
  background: '#059669', color: '#fff', border: 'none',
  borderRadius: 6, padding: '8px 16px', fontSize: 13,
  fontWeight: 600, cursor: 'pointer', marginTop: 4,
};

const historyRowStyle = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '8px 12px', background: '#f9fafb',
  border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 6,
};

const linkBtnStyle = {
  background: 'none', border: 'none', color: '#3b82f6',
  cursor: 'pointer', fontSize: 13, fontWeight: 600,
  padding: 0, marginBottom: 12,
};
